import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useStringsContext } from '../../../../contexts/strings';
import adminApi from '../../../../api/admin-api';
import { useUserAuth } from '../../../../contexts/auth';
import { Announcement } from '../types';

export type AnnouncementForm = Partial<Omit<Announcement, 'id' | 'createdAt' | 'updatedAt'>>;
export const useAnnouncements = () => {
  const { currentUser } = useUserAuth();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { strings } = useStringsContext();

  const onError = () => {
    enqueueSnackbar(strings.messages.loadingError, {
      variant: 'error',
    });
  };

  // GET all announcements
  const query = useQuery({
    queryKey: ['announcements'],
    queryFn: async (): Promise<Announcement[]> => {
      const response = await adminApi.get({
        path: `/announcements`,
        auth: await currentUser?.getIdToken(),
      });
      return await response.json();
    },
  });

  // CREATE announcement
  const createMutation = useMutation({
    mutationFn: async (data: AnnouncementForm) => {
      const errorMessage = 'Creating announcement failed!';
      try {
        const response = await adminApi.post({
          path: `/announcements`,
          body: data,
          auth: await currentUser?.getIdToken(),
        });
        if (!response.ok) {
          throw new Error(`${errorMessage} (${response.status})`);
        }
        return await response.json();
      } catch (error) {
        console.error(error);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch announcements
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      query.refetch();
    },
    onError,
  });

  // UPDATE announcement
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: AnnouncementForm }) => {
      const errorMessage = 'Updating announcement failed!';
      try {
        const response = await adminApi.put({
          path: `/announcements/${id}`,
          body: data,
          auth: await currentUser?.getIdToken(),
        });
        if (!response.ok) {
          throw new Error(`${errorMessage} (${response.status})`);
        }
        return await response.json();
      } catch (error) {
        console.error(error);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      query.refetch();
    },
    onError,
  });

  // DELETE announcement
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const errorMessage = 'Deleting announcement failed!';
      try {
        const response = await adminApi.delete({
          path: `/announcements/${id}`,
          auth: await currentUser?.getIdToken(),
        });
        if (!response.ok) {
          throw new Error(`${errorMessage} (${response.status})`);
        }
        return await response.json();
      } catch (error) {
        console.error(error);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      query.refetch();
    },
    onError,
  });

  return {
    // Query
    announcements: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,

    // Create
    createAnnouncement: createMutation.mutate,
    createMutation,

    // Update
    updateAnnouncement: updateMutation.mutate,
    updateMutation,

    // Delete
    deleteAnnouncement: deleteMutation.mutate,
    deleteMutation,
  };
};
